import React from 'react';
import { User, Mail, Phone, MapPin, Calendar, BookOpen, Building, Target, GraduationCap, Users, X } from 'lucide-react';
import { SchoolStudent } from '../types';

interface SchoolStudentDetailProps {
  student: SchoolStudent;
  onClose?: () => void;
}

const SchoolStudentDetail: React.FC<SchoolStudentDetailProps> = ({ student, onClose }) => {
  const getGenderLabel = (gender: string) => {
    switch (gender) {
      case 'male': return 'Nam';
      case 'female': return 'Nữ';
      case 'other': return 'Khác';
      default: return gender;
    }
  };

  const getGenderColor = (gender: string) => {
    switch (gender) {
      case 'male': return 'bg-blue-100 text-blue-800';
      case 'female': return 'bg-pink-100 text-pink-800';
      case 'other': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'graduated':
        return 'bg-blue-100 text-blue-800';
      case 'transferred':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Đang học';
      case 'inactive':
        return 'Nghỉ học';
      case 'graduated':
        return 'Đã tốt nghiệp';
      case 'transferred':
        return 'Chuyển trường';
      default:
        return status;
    }
  };

  const getAcademicLevelLabel = (level: string) => {
    switch (level) {
      case 'elementary': return 'Tiểu học';
      case 'middle': return 'Trung học cơ sở';
      case 'high': return 'Trung học phổ thông';
      default: return level;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mr-4">
            <User className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{student.name}</h2>
            <p className="text-gray-600">{student.grade ? `Lớp ${student.grade}` : 'Học sinh'}</p>
            <div className="flex items-center gap-4 mt-2">
              {student.gender && (
                <span className={`px-2 py-1 text-xs rounded-full ${getGenderColor(student.gender)}`}>
                  {getGenderLabel(student.gender)}
                </span>
              )}
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(student.status)}`}>
                {getStatusText(student.status)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-4">
          {student.code && (
            <div className="text-right">
              <p className="text-sm text-gray-500">Mã học sinh</p>
              <p className="text-lg font-mono font-semibold text-gray-900">{student.code}</p>
            </div>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              title="Đóng"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="lg:col-span-2">
          <div className="space-y-6">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin liên hệ</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900">{student.email}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Điện thoại</p>
                    <p className="text-gray-900">{student.phone}</p>
                  </div>
                </div>
                {student.address && (
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Địa chỉ</p>
                      <p className="text-gray-900">{student.address}</p>
                    </div>
                  </div>
                )}
                {student.dateOfBirth && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Ngày sinh</p>
                      <p className="text-gray-900">
                        {new Date(student.dateOfBirth).toLocaleDateString('vi-VN')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Parent Information */}
            {(student.parentName || student.parentPhone || student.parentEmail) && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin phụ huynh</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {student.parentName && (
                    <div className="flex items-center">
                      <Users className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Họ tên phụ huynh</p>
                        <p className="text-gray-900">{student.parentName}</p>
                      </div>
                    </div>
                  )}
                  {student.parentPhone && (
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Điện thoại phụ huynh</p>
                        <p className="text-gray-900">{student.parentPhone}</p>
                      </div>
                    </div>
                  )}
                  {student.parentEmail && (
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Email phụ huynh</p>
                        <p className="text-gray-900">{student.parentEmail}</p>
                      </div>
                    </div>
                  )}
                  {student.emergencyContact && (
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Liên hệ khẩn cấp</p>
                        <p className="text-gray-900">{student.emergencyContact}</p>
                        {student.emergencyPhone && (
                          <p className="text-sm text-gray-600">{student.emergencyPhone}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Academic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin học tập</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {student.grade && (
                  <div className="flex items-center">
                    <BookOpen className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Khối lớp</p>
                      <p className="text-gray-900">Lớp {student.grade}</p>
                    </div>
                  </div>
                )}
                {student.schoolYear && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Năm học</p>
                      <p className="text-gray-900">{student.schoolYear}</p>
                    </div>
                  </div>
                )}
                {student.enrollmentDate && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Ngày nhập học</p>
                      <p className="text-gray-900">
                        {new Date(student.enrollmentDate).toLocaleDateString('vi-VN')}
                      </p>
                    </div>
                  </div>
                )}
                {student.graduationDate && (
                  <div className="flex items-center">
                    <GraduationCap className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Ngày tốt nghiệp</p>
                      <p className="text-gray-900">
                        {new Date(student.graduationDate).toLocaleDateString('vi-VN')}
                      </p>
                    </div>
                  </div>
                )}
                {student.academicLevel && (
                  <div className="flex items-center">
                    <Target className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Trình độ học vấn</p>
                      <p className="text-gray-900">{getAcademicLevelLabel(student.academicLevel)}</p>
                    </div>
                  </div>
                )}
                {student.gpa && student.gpa > 0 && (
                  <div className="flex items-center">
                    <Target className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Điểm GPA</p>
                      <p className="text-gray-900">{student.gpa.toFixed(2)}</p>
                    </div>
                  </div>
                )}
                {student.attendance && student.attendance > 0 && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Tỷ lệ chuyên cần</p>
                      <p className="text-gray-900">{student.attendance}%</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Subjects */}
            {student.subjects && student.subjects.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Môn học</h3>
                <div className="flex flex-wrap gap-2">
                  {student.subjects.map((subject) => (
                    <span key={subject} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Class Assignment */}
          {student.className && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Lớp học</h4>
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Tên lớp</p>
                  <p className="text-gray-900 font-medium">{student.className}</p>
                </div>
                {student.grade && (
                  <div>
                    <p className="text-sm text-gray-500">Khối</p>
                    <p className="text-gray-900">Lớp {student.grade}</p>
                  </div>
                )}
                {student.schoolYear && (
                  <div>
                    <p className="text-sm text-gray-500">Năm học</p>
                    <p className="text-gray-900">{student.schoolYear}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* School Information */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Thông tin trường</h4>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500">Tên trường</p>
                <p className="text-gray-900 font-medium">{student.schoolName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Mã trường</p>
                <p className="text-gray-900 font-mono">{student.schoolId}</p>
              </div>
            </div>
          </div>

          {/* Academic Stats */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Thống kê học tập</h4>
            <div className="space-y-3">
              {student.gpa && student.gpa > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Điểm GPA</span>
                  <span className="font-semibold text-gray-900">{student.gpa.toFixed(2)}</span>
                </div>
              )}
              {student.attendance && student.attendance > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Chuyên cần</span>
                  <span className="font-semibold text-gray-900">{student.attendance}%</span>
                </div>
              )}
              {student.subjects && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Số môn học</span>
                  <span className="font-semibold text-gray-900">{student.subjects.length}</span>
                </div>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Thông tin hệ thống</h4>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500">Ngày tạo</p>
                <p className="text-gray-900 text-sm">
                  {new Date(student.createdAt).toLocaleDateString('vi-VN')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Cập nhật lần cuối</p>
                <p className="text-gray-900 text-sm">
                  {new Date(student.updatedAt).toLocaleDateString('vi-VN')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolStudentDetail;
